import React, { useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Button, CircularProgress, Typography } from '@material-ui/core';
import {
  Notes as NotesIcon,
  Visibility as VisibilityIcon
} from '@material-ui/icons';

import HoveredCard from '../../../common/card/HoveredCard';
import { formatImgURLs } from '../../../common/inline/InlineHtml.hooks';
import { useUserContext } from '../../../user/User.context';
import { useUserPermissions } from '../../../user/useUserPermissions';
import TaskStatusVersionCard from '../../tasks/cards/TaskStatusVersionCard';
import { useGetFileVersion } from '../Document.hook';
import { FileDto } from '../Document.interface';
import DocumentDownloadButton from './DocumentDownloadButton';

export interface DocumentLogStatusCardProps {
  document: FileDto;
}

const DocumentLogStatusCard: React.FC<DocumentLogStatusCardProps> = ({
  document
}) => {
  if (!document.task) {
    return <></>;
  }
  return (
    <TaskStatusVersionCard
      taskEntity={document}
      renderContent={(group, latestStatusLog, isExpanded) => (
        <DocumentLogCard
          document={document}
          version={group.version}
          isExpanded={isExpanded}
        />
      )}
    />
  );
};

export interface DocumentLogCardProps {
  document: FileDto;
  version: number;
  isExpanded: boolean;
}

const DocumentLogCard: React.FC<DocumentLogCardProps> = ({
  document,
  version,
  isExpanded
}) => {
  console.log('isExpanded', isExpanded);
  const { user } = useUserContext();
  const { isDocHistoryViewer } = useUserPermissions(user);
  const [{ data, isLoading }, get] = useGetFileVersion(document.id, version);
  useEffect(() => void (isExpanded && get()), [get, isExpanded]);

  const storageKey: string = data?.storageKey || '';
  const content: string | undefined = useMemo(() => {
    const { descriptionOfChange } = data || {};
    return descriptionOfChange && formatImgURLs(descriptionOfChange);
  }, [data]);
  return (
    <HoveredCard
      icon={<NotesIcon />}
      title='Description of Change'
      content={
        isLoading ? (
          <CircularProgress size={20} />
        ) : (
          content && (
            <Typography
              variant='body1'
              dangerouslySetInnerHTML={{ __html: content }}
              style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            />
          )
        )
      }
      // set to undefined to avoid rendering CardActions
      actions={
        !isDocHistoryViewer ? undefined : (
          <>
            <Button
              variant='text'
              color='primary'
              component={RouterLink}
              startIcon={<VisibilityIcon />}
              to={`/portal/documents/file/task/${document.task?.id}?ver=${version}`}
            >
              View
            </Button>
            <DocumentDownloadButton
              asStartIcon
              storageKey={storageKey}
              buttonProps={{ variant: 'text' }}
              title={`${document.name} v${version}`}
            />
          </>
        )
      }
    />
  );
};

export default DocumentLogStatusCard;
